<template>
  <div class="gd">
    <div
      v-if="loading"
      class="gd-loading"
    >
      <feather-icon
        class="spinner"
        icon="RefreshCwIcon"
      />
      <span>Loading…</span>
    </div>

    <template v-else>
      <div class="gd-stack">
        <ds-note variant="quiet">
          Group dates are tables of 10 starting at 20:00. We reserve under a table name (e.g. Table A) rather than
          guest names. The table name is what people use to check in at your venue. For now we only run group dates
          on Thursday, Friday and Saturday in Central London, and we're expanding to more days and areas soon.
        </ds-note>

        <ds-card
          title="Receive group date bookings"
          subtitle="Turn this on to be included when we allocate group dates. Turning it off clears your selected days."
        >
          <template #actions>
            <ds-switch
              :value="groupDateEnabled"
              aria-label="Receive group date bookings"
              @change="onToggle"
            />
          </template>
        </ds-card>

        <transition name="fade">
          <ds-card
            v-if="groupDateEnabled"
            title="Days &amp; tables"
            subtitle="Pick the days you'd like group date bookings on, then set how many tables of 10 you can offer on each."
          >
            <div
              v-for="(day, index) in days"
              :key="index"
              class="gd-day"
            >
              <input
                :id="`gd-day-${index}`"
                type="checkbox"
                class="gd-day-check"
                :checked="dayConfig[index].enabled"
                @change="toggleDay(index)"
              >
              <label
                :for="`gd-day-${index}`"
                class="gd-day-name"
                :class="{ off: !dayConfig[index].enabled }"
              >{{ day }}</label>

              <span
                v-if="!dayConfig[index].enabled"
                class="gd-day-empty"
              >No group dates</span>

              <div
                v-else
                class="gd-stepper-wrap"
              >
                <div class="gd-stepper">
                  <button
                    type="button"
                    class="gd-step-btn"
                    aria-label="Fewer tables"
                    :disabled="dayConfig[index].tables <= minTables"
                    @click="setTables(index, dayConfig[index].tables - 1)"
                  >−</button>
                  <input
                    type="number"
                    class="gd-step-val"
                    :aria-label="`Tables on ${day}`"
                    :min="minTables"
                    :max="maxTables"
                    :value="dayConfig[index].tables"
                    @input="setTables(index, parseInt($event.target.value, 10))"
                  >
                  <button
                    type="button"
                    class="gd-step-btn"
                    aria-label="More tables"
                    :disabled="dayConfig[index].tables >= maxTables"
                    @click="setTables(index, dayConfig[index].tables + 1)"
                  >+</button>
                </div>
                <span class="gd-step-unit">tables of 10</span>
              </div>
            </div>
          </ds-card>
        </transition>

        <transition name="fade">
          <ds-card
            v-if="groupDateEnabled"
            title="Reservation preference"
            subtitle="Tell us how you'd like to receive each group date reservation."
          >
            <ds-field
              label="Reservation method"
              label-for="gd-method"
              width="md"
            >
              <ds-select
                id="gd-method"
                v-model="reservationMethod"
                :options="reservationOptions"
              />
            </ds-field>

            <ds-field
              v-if="reservationMethod === 'email'"
              label="Email address for bookings"
              label-for="gd-email"
              width="lg"
              :error="visibleErrors.email"
            >
              <template #default="{ describedBy }">
                <ds-input
                  id="gd-email"
                  v-model.trim="reservationEmail"
                  type="email"
                  placeholder="e.g. bookings@yourrestaurant.com"
                  :invalid="!!visibleErrors.email"
                  :aria-describedby="describedBy"
                  @blur="touch('email')"
                />
              </template>
            </ds-field>

            <ds-field
              v-if="reservationMethod === 'sms'"
              label="Phone number for bookings"
              label-for="gd-phone"
              width="md"
              :error="visibleErrors.phone"
            >
              <template #default="{ describedBy }">
                <ds-input
                  id="gd-phone"
                  v-model.trim="reservationPhone"
                  type="tel"
                  placeholder="e.g. +31 6 12345678"
                  :invalid="!!visibleErrors.phone"
                  :aria-describedby="describedBy"
                  @blur="touch('phone')"
                />
              </template>
            </ds-field>

            <ds-note
              v-if="reservationMethod === 'bookable'"
              variant="warn"
            >
              Make sure to toggle Breeze in Bookable and confirm it via email.
            </ds-note>

            <template v-if="needsLink">
              <ds-note variant="warn">
                <template v-if="isDmnSevenroomsOpentable">
                  Add a booking link that lets us reserve tables for 10 people. Please make sure there are no
                  payment requirements and that we can cancel the reservation directly. You can create a dedicated
                  booking type just for Breeze from your reservation system.
                </template>
                <template v-else>
                  Make sure your booking link allows reservations for 10 people. If not, please create a new booking
                  link with no payment requirements that allows cancellation.
                </template>
              </ds-note>

              <ds-field
                label="Booking link"
                label-for="gd-link"
                width="lg"
                :error="visibleErrors.link"
              >
                <template #default="{ describedBy }">
                  <ds-input
                    id="gd-link"
                    v-model.trim="reservationDirectLink"
                    type="url"
                    placeholder="e.g. https://yourrestaurant.com/book"
                    :invalid="!!visibleErrors.link"
                    :aria-describedby="describedBy"
                    @blur="touch('link')"
                  />
                </template>
              </ds-field>
            </template>
          </ds-card>
        </transition>
      </div>

      <ds-save-bar>
        <span
          class="gd-button-wrap"
          :data-tooltip="errors.method"
        >
          <ds-button
            :disabled="saving || shouldDisableSaveButton"
            @click="save"
          >
            {{ saving ? 'Saving…' : 'Save group date preferences' }}
          </ds-button>
        </span>
      </ds-save-bar>
    </template>
  </div>
</template>

<script>
import {
  DsButton, DsCard, DsField, DsInput, DsNote, DsSaveBar, DsSelect, DsSwitch,
} from '@/design-system/components'
import { requests } from '@/_helpers/requests'
import { emailError, phoneError, linkError } from './contact-validators'

export default {
  name: 'GroupDateAvailability',
  components: {
    DsButton,
    DsCard,
    DsField,
    DsInput,
    DsNote,
    DsSaveBar,
    DsSelect,
    DsSwitch,
  },
  props: {
    locationId: {
      // Empty until the partner finishes loading; the watcher only fetches once set.
      type: String,
      default: () => '',
    },
  },
  data() {
    return {
      days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      groupDateEnabled: false,
      dayConfig: Array.from({ length: 7 }, () => ({ enabled: false, tables: 1 })),
      minTables: 1,
      maxTables: 50,
      reservationMethod: null,
      reservationEmail: '',
      // Errors stay hidden until blur or a save attempt, so half-typed input isn't flagged.
      touched: { email: false, phone: false, link: false },
      reservationPhone: '',
      reservationDirectLink: '',
      reservationOptions: [
        { value: null, text: 'Select a reservation method', disabled: true },
        { value: 'email', text: 'Email' },
        { value: 'sms', text: 'SMS' },
        { value: 'zenchef', text: 'Zenchef' },
        { value: 'formitable', text: 'Formitable' },
        { value: 'design-my-night', text: 'Design My Night' },
        { value: 'guestplan', text: 'Guestplan' },
        { value: 'bookable', text: 'Bookable' },
        { value: 'sevenrooms', text: 'Sevenrooms' },
        { value: 'open-table', text: 'OpenTable' },
        { value: 'custom', text: 'Other' },
      ],
      saving: false,
      loading: true,
    }
  },
  computed: {
    isDmnSevenroomsOpentable() {
      return ['design-my-night', 'sevenrooms', 'open-table'].includes(this.reservationMethod)
    },
    isZenchefFormitableGuestplan() {
      return ['zenchef', 'formitable', 'guestplan'].includes(this.reservationMethod)
    },
    // 'bookable' and 'custom' render no link field and send none, so they aren't validated.
    needsLink() {
      return this.isDmnSevenroomsOpentable || this.isZenchefFormitableGuestplan
    },
    // Why each field is unusable, or null, including when the method doesn't use it.
    errors() {
      return {
        method: this.groupDateEnabled && !this.reservationMethod
          ? 'Select how you would like to receive group date reservations.'
          : null,
        email: this.groupDateEnabled && this.reservationMethod === 'email'
          ? emailError(this.reservationEmail, { requiredMessage: 'Enter the email address we should send group date bookings to.' })
          : null,
        phone: this.groupDateEnabled && this.reservationMethod === 'sms'
          ? phoneError(this.reservationPhone, { requiredMessage: 'Enter the phone number we should text group date bookings to.' })
          : null,
        link: this.groupDateEnabled && this.needsLink
          ? linkError(this.reservationDirectLink, { requiredMessage: 'Add the booking link we should use to reserve your tables.' })
          : null,
      }
    },
    // Only touched fields show their message.
    visibleErrors() {
      const { email, phone, link } = this.errors
      return {
        email: this.touched.email ? email : null,
        phone: this.touched.phone ? phone : null,
        link: this.touched.link ? link : null,
      }
    },
    // What the save toast leads with; null means good to send.
    blockingError() {
      return this.errors.method || this.errors.email || this.errors.phone || this.errors.link || null
    },
    shouldDisableSaveButton() {
      return !!this.errors.method
    },
  },
  watch: {
    // Don't carry a stale error into a freshly shown field.
    reservationMethod() {
      this.touched = { email: false, phone: false, link: false }
    },
    locationId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchGroupDate()
      },
    },
  },
  methods: {
    fetchGroupDate() {
      this.loading = true
      this.touched = { email: false, phone: false, link: false }
      this.dayConfig = Array.from({ length: 7 }, () => ({ enabled: false, tables: 1 }))
      requests.get(`partner-portal/locations/${this.locationId}/group-date-settings`)
        .then(response => {
          const data = response.data
          this.groupDateEnabled = data.groupDateEnabled || false
          this.reservationMethod = data.reservationMethod || null
          this.reservationEmail = data.reservationEmail || ''
          this.reservationPhone = data.reservationPhone || ''
          this.reservationDirectLink = data.reservationDirectLink || ''
          // The API returns `weekday` but expects `day` on write; see save().
          if (data.groupDateDays) {
            data.groupDateDays.forEach(d => {
              this.$set(this.dayConfig, d.weekday, {
                enabled: true,
                tables: d.tables || 1,
              })
            })
          }
        })
        .catch(() => {
          this.groupDateEnabled = false
        })
        .finally(() => {
          this.loading = false
        })
    },
    touch(field) {
      this.touched = { ...this.touched, [field]: true }
    },
    onToggle(checked) {
      this.groupDateEnabled = checked
      if (!checked) {
        this.dayConfig = Array.from({ length: 7 }, () => ({ enabled: false, tables: 1 }))
      }
    },
    toggleDay(dayIndex) {
      const current = this.dayConfig[dayIndex]
      this.$set(this.dayConfig, dayIndex, {
        enabled: !current.enabled,
        tables: current.enabled ? 1 : current.tables,
      })
    },
    // Clamp to range; a cleared input reads as NaN.
    setTables(dayIndex, value) {
      const tables = Number.isNaN(value)
        ? this.minTables
        : Math.min(this.maxTables, Math.max(this.minTables, value))
      this.$set(this.dayConfig, dayIndex, { ...this.dayConfig[dayIndex], tables })
    },
    save() {
      // Don't send contact details we know won't work.
      if (this.blockingError) {
        this.touched = { email: true, phone: true, link: true }
        this.$notify.blocked(this.blockingError)
        return
      }
      this.saving = true
      // Written as `day`, read back as `weekday`. That asymmetry is the endpoint's contract.
      const groupDateDays = this.dayConfig
        .map((d, index) => ({ day: index, tables: d.tables }))
        .filter((_, index) => this.dayConfig[index].enabled)

      requests.put(`partner-portal/locations/${this.locationId}/group-date-settings`, {
        groupDateEnabled: this.groupDateEnabled,
        groupDateDays,
        reservationMethod: this.reservationMethod,
        reservationEmail: this.reservationMethod === 'email' ? this.reservationEmail : null,
        reservationPhone: this.reservationMethod === 'sms' ? this.reservationPhone : null,
        // Gated on needsLink so a method with no link field can't post a stale, unvalidated one.
        reservationDirectLink: this.needsLink ? this.reservationDirectLink : null,
      })
        .then(() => {
          this.saving = false
          this.$notify.success('Group date preferences saved successfully.')
        })
        .catch(() => {
          this.saving = false
          this.$notify.error('Failed to save group date preferences. Please try again.')
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/design-system/mixins';

.gd {
  color: $c-text;
}

.gd-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 0;
  font-size: 13px;
  color: $c-text-sec;
}

.gd-stack {
  @include ds-stack;
}

.gd-day {
  @include ds-inset-row;

  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  margin-bottom: 10px;

  &:last-child { margin-bottom: 0; }
}

.gd-day-check {
  width: 17px;
  height: 17px;
  accent-color: $c-wine;
  cursor: pointer;
  flex-shrink: 0;
}

.gd-day-name {
  width: 108px;
  margin: 0;
  font-weight: 700;
  font-size: 15.5px;
  color: $c-text;
  cursor: pointer;

  &.off { color: $c-text-ter; }
}

.gd-day-empty {
  font-size: 12px;
  color: $c-text-ter;
  font-style: italic;
}

.gd-stepper-wrap {
  display: flex;
  align-items: center;
  gap: 9px;
}

.gd-stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid $c-border;
  border-radius: 9px;
  background: $c-white;
  overflow: hidden;
}

.gd-step-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: $c-white;
  color: $c-wine;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) { background: $c-wine-light; }

  &:disabled {
    color: $c-text-ter;
    cursor: not-allowed;
  }
}

.gd-step-val {
  width: 44px;
  height: 30px;
  border: none;
  border-left: 1px solid $c-border-light;
  border-right: 1px solid $c-border-light;
  text-align: center;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  color: $c-text;
  background: $c-white;
  appearance: textfield;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    background: $c-wine-light;
  }
}

.gd-step-unit {
  font-size: 12px;
  color: $c-text-sec;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.gd-button-wrap {
  position: relative;
  display: inline-block;

  &[data-tooltip]:hover::before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    right: 0;
    padding: 6px 10px;
    margin-bottom: 8px;
    background: $c-text;
    color: $c-white;
    font-size: 12px;
    border-radius: 4px;
    // Anchored right because the save bar sits at the viewport edge.
    white-space: normal;
    width: max-content;
    max-width: min(260px, 90vw);
    text-align: center;
    line-height: 1.4;
    pointer-events: none;
    z-index: 10;
  }
}
</style>
